import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box, Grid, Card, CardContent, Typography, Chip,
  Stack, Button, Divider, Avatar,
} from '@mui/material'
import {
  Business, People, Receipt, TrendingUp,
  ArrowForward, CheckCircle, Devices, WarningAmber,
} from '@mui/icons-material'
import {
  useAdminTenants, usePlatformAdmins,
  useAdminSubscriptions, usePricingTiers,
  type AdminSubscription,
} from '../../hooks/usePlatformAdmin'
import { format } from 'date-fns'
import { AdminDataError } from '../../components/AdminDataError'
import { AdminPageHeader } from '../../components/AdminPageHeader'
import { AdminLoadingState } from '../../components/AdminLoadingState'

interface StatCardProps {
  label: string
  value: string | number
  sub?: string
  icon: React.ReactNode
  iconBg: string
  iconColor: string
  trend?: string
}

function StatCard({ label, value, sub, icon, iconBg, iconColor, trend }: StatCardProps) {
  return (
    <Card sx={{
      bgcolor: '#ffffff',
      border: '1px solid #e2e8f0',
      borderRadius: 2,
      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      transition: 'box-shadow 0.15s, transform 0.15s',
      '&:hover': { boxShadow: '0 6px 20px rgba(0,0,0,0.08)', transform: 'translateY(-1px)' },
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: 11, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              {label}
            </Typography>
            <Typography variant="h3" fontWeight={800} sx={{ color: '#0f172a', mt: 0.5, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
              {value}
            </Typography>
            {sub && <Typography variant="caption" sx={{ color: '#94a3b8', mt: 0.5, display: 'block' }}>{sub}</Typography>}
            {trend && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                <TrendingUp sx={{ fontSize: 13, color: '#10b981' }} />
                <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 600, fontSize: 11 }}>{trend}</Typography>
              </Box>
            )}
          </Box>
          <Box sx={{
            width: 46, height: 46, borderRadius: 2, bgcolor: iconBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Box sx={{ color: iconColor, display: 'flex' }}>{icon}</Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

// Normalize subscription total_amount to monthly for MRR
function monthlyAmount(sub: AdminSubscription): number {
  if (sub.status !== 'active' && sub.status !== 'grace') return 0
  return sub.billing_cycle === 'yearly' ? sub.total_amount / 12 : sub.total_amount
}

export default function AdminOverviewPage() {
  const navigate = useNavigate()
  const tenantsQuery = useAdminTenants()
  const adminsQuery = usePlatformAdmins()
  const subscriptionsQuery = useAdminSubscriptions()
  const tiersQuery = usePricingTiers()
  const { data: tenants = [], isLoading } = tenantsQuery
  const { data: admins = [] } = adminsQuery
  const { data: subscriptions = [] } = subscriptionsQuery
  const { data: tiers = [] } = tiersQuery

  const activeTenants = tenants.filter((t: { status: string }) => t.status === 'active')
  const hasDataError = tenantsQuery.isError || adminsQuery.isError || subscriptionsQuery.isError || tiersQuery.isError

  // Index subscriptions by tenant
  const subsByTenant = useMemo(() => {
    const map = new Map<string, AdminSubscription>()
    for (const s of subscriptions) map.set(s.tenant_id, s)
    return map
  }, [subscriptions])

  // MRR from real subscriptions
  const mrr = useMemo(() =>
    subscriptions.reduce((sum, s) => sum + monthlyAmount(s), 0),
  [subscriptions])

  // Revenue by tier — match subscription price_per_device to pricing tier ranges
  const revenueByTier = useMemo(() => {
    const activeSubs = subscriptions.filter((s) => s.status === 'active' || s.status === 'grace')
    if (tiers.length === 0) {
      // No tiers configured — show single "All" bucket
      const total = activeSubs.reduce((sum, s) => sum + monthlyAmount(s), 0)
      return [{ label: 'All Subscriptions', count: activeSubs.length, revenue: total, color: '#34A8E0' }]
    }

    const tierColors = ['#3b82f6', '#34A8E0', '#64748b', '#10b981', '#f59e0b', '#ef4444']
    return tiers.map((tier, i) => {
      const matching = activeSubs.filter((s) =>
        s.device_count >= tier.min_devices &&
        (tier.max_devices == null || s.device_count <= tier.max_devices)
      )
      const revenue = matching.reduce((sum, s) => sum + monthlyAmount(s), 0)
      return {
        label: tier.name,
        count: matching.length,
        revenue,
        color: tierColors[i % tierColors.length],
      }
    })
  }, [subscriptions, tiers])

  const totalDevices = useMemo(() =>
    subscriptions.filter((s) => s.status === 'active' || s.status === 'grace')
      .reduce((sum, s) => sum + s.device_count, 0),
  [subscriptions])

  const subscribedTenantCount = useMemo(
    () => new Set(subscriptions.map((s) => s.tenant_id)).size,
    [subscriptions],
  )

  if (isLoading) {
    return <AdminLoadingState />
  }

  return (
    <Box>
      <AdminPageHeader
        eyebrow="Command center"
        title="Platform Overview"
        description={`${format(new Date(), 'EEEE, MMMM d, yyyy')} · Live operational and subscription data.`}
        icon={<TrendingUp />}
        actions={<Button variant="contained" startIcon={<Business />} onClick={() => navigate('/admin/tenants')}>Manage tenants</Button>}
      />

      {hasDataError && (
        <AdminDataError
          message="Some platform data could not be loaded. Dashboard totals may be incomplete."
          onRetry={() => {
            void tenantsQuery.refetch()
            void adminsQuery.refetch()
            void subscriptionsQuery.refetch()
            void tiersQuery.refetch()
          }}
        />
      )}

      {/* KPI */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Total Tenants" value={tenants.length} sub={`${activeTenants.length} active`}
            icon={<Business />} iconBg="#eff6ff" iconColor="#3b82f6" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Monthly Revenue" value={`$${Math.round(mrr).toLocaleString()}`} sub="from active subscriptions"
            trend={mrr > 0 ? `$${Math.round(mrr * 12).toLocaleString()} ARR` : undefined}
            icon={<Receipt />} iconBg="#f0fdf4" iconColor="#10b981" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Licensed Devices" value={totalDevices} sub="across active plans"
            icon={<Devices />} iconBg="#EBF6FD" iconColor="#34A8E0" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Platform Admins" value={admins.length} sub="SaaS owners"
            icon={<People />} iconBg="#fff7ed" iconColor="#f97316" />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        {/* Recent tenants */}
        <Grid item xs={12} md={7}>
          <Card sx={{ bgcolor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={700} color="#0f172a" sx={{ fontSize: 15 }}>
                  Recent Tenants
                </Typography>
                <Button size="small" endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
                  onClick={() => navigate('/admin/tenants')}
                  sx={{ color: '#34A8E0', fontSize: 12, '&:hover': { bgcolor: '#EBF6FD' } }}>
                  View all
                </Button>
              </Box>

              <Stack spacing={0.25}>
                {tenants.slice(0, 7).map((t: { id: string; name: string; status: string; created_at: string }) => {
                  const sub = subsByTenant.get(t.id)
                  return (
                    <Box
                      key={t.id}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        px: 1.5, py: 1, borderRadius: 1.5,
                        transition: 'background 0.1s',
                        '&:hover': { bgcolor: '#f8fafc' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                        <Avatar sx={{
                          width: 32, height: 32, fontSize: 13, fontWeight: 700,
                          bgcolor: sub ? '#EBF6FD' : '#f8fafc',
                          color: sub ? '#34A8E0' : '#94a3b8',
                          borderRadius: 1.5, flexShrink: 0,
                        }}>
                          {t.name[0]?.toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" color="#1e293b" fontWeight={500} noWrap>{t.name}</Typography>
                          <Typography variant="caption" color="#94a3b8" sx={{ fontSize: 11 }}>
                            {t.created_at ? format(new Date(t.created_at), 'MMM d, yyyy') : '\u2014'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
                        {sub ? (
                          <Chip label={`${sub.device_count} dev · $${Math.round(monthlyAmount(sub))}/mo`} size="small" sx={{
                            bgcolor: '#EBF6FD', color: '#34A8E0',
                            border: '1px solid #B3DCF2', fontSize: 10, fontWeight: 600,
                          }} />
                        ) : (
                          <Chip label="No plan" size="small" sx={{
                            bgcolor: '#f8fafc', color: '#94a3b8',
                            border: '1px solid #e2e8f0', fontSize: 10, fontWeight: 500,
                          }} />
                        )}
                        <Chip
                          label={t.status} size="small"
                          sx={{
                            bgcolor: t.status === 'active' ? '#f0fdf4' : '#fff1f2',
                            color: t.status === 'active' ? '#10b981' : '#ef4444',
                            border: `1px solid ${t.status === 'active' ? '#bbf7d0' : '#fecaca'}`,
                            fontSize: 11, fontWeight: 500,
                          }}
                        />
                      </Box>
                    </Box>
                  )
                })}
                {tenants.length === 0 && (
                  <Box sx={{ py: 5, textAlign: 'center' }}>
                    <Business sx={{ color: '#e2e8f0', fontSize: 40, mb: 1 }} />
                    <Typography variant="body2" color="#94a3b8">No tenants yet</Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Revenue by Tier + health */}
        <Grid item xs={12} md={5}>
          <Stack spacing={2.5}>
            <Card sx={{ bgcolor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} color="#0f172a" sx={{ fontSize: 15, mb: 2 }}>
                  Revenue by Tier
                </Typography>
                {revenueByTier.length === 0 ? (
                  <Typography variant="body2" color="#94a3b8">No active subscriptions</Typography>
                ) : (
                  <Stack spacing={1.75}>
                    {revenueByTier.map(({ label, count, revenue, color }) => {
                      const pct = mrr > 0 ? Math.min(100, (revenue / mrr) * 100) : 0
                      return (
                        <Box key={label}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
                              <Typography variant="body2" color="#475569" fontWeight={500}>{label}</Typography>
                              <Typography variant="caption" color="#94a3b8">{count} tenants</Typography>
                            </Box>
                            <Typography variant="body2" color="#0f172a" fontWeight={700}>${Math.round(revenue).toLocaleString()}</Typography>
                          </Box>
                          <Box sx={{ height: 5, borderRadius: 3, bgcolor: '#f1f5f9', overflow: 'hidden' }}>
                            <Box sx={{
                              height: '100%', borderRadius: 3, width: `${pct}%`, bgcolor: color,
                              transition: 'width 0.8s cubic-bezier(0.4,0,0.2,1)',
                            }} />
                          </Box>
                        </Box>
                      )
                    })}
                  </Stack>
                )}
                <Divider sx={{ borderColor: '#f1f5f9', mt: 2, mb: 1.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="#64748b">Monthly Total</Typography>
                  <Typography variant="body2" color="#0f172a" fontWeight={700}>${Math.round(mrr).toLocaleString()}/mo</Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Subscription health */}
            <Card sx={{ bgcolor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} color="#0f172a" sx={{ fontSize: 15, mb: 1.5 }}>
                  Subscription Health
                </Typography>
                <Stack spacing={1}>
                  {[
                    { label: 'Active', count: subscriptions.filter((s) => s.status === 'active').length, color: '#10b981', bg: '#f0fdf4' },
                    { label: 'Grace Period', count: subscriptions.filter((s) => s.status === 'grace').length, color: '#f59e0b', bg: '#fffbeb' },
                    { label: 'Expired', count: subscriptions.filter((s) => s.status === 'expired').length, color: '#ef4444', bg: '#fef2f2' },
                    { label: 'No Plan', count: Math.max(0, tenants.length - subscribedTenantCount), color: '#94a3b8', bg: '#f8fafc' },
                  ].map(({ label, count, color, bg }) => (
                    <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1.5, py: 0.75, borderRadius: 1, bgcolor: count > 0 ? bg : 'transparent' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color }} />
                        <Typography variant="body2" color="#475569" fontWeight={500}>{label}</Typography>
                      </Box>
                      <Typography variant="body2" color={count > 0 ? color : '#94a3b8'} fontWeight={700}>{count}</Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>

            <Card sx={{ bgcolor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
              <CardContent sx={{ py: '14px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: 1.5,
                    bgcolor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {hasDataError
                      ? <WarningAmber sx={{ color: '#f59e0b', fontSize: 20 }} />
                      : <CheckCircle sx={{ color: '#10b981', fontSize: 20 }} />}
                  </Box>
                  <Box>
                    <Typography variant="body2" color="#0f172a" fontWeight={600}>
                      {hasDataError ? 'Dashboard Data Incomplete' : 'Dashboard Data Connected'}
                    </Typography>
                    <Typography variant="caption" color="#94a3b8">
                      {hasDataError ? 'One or more required APIs did not respond' : 'Tenant, admin, and billing APIs responded'}
                    </Typography>
                  </Box>
                  <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: hasDataError ? '#f59e0b' : '#10b981' }} />
                    <Typography variant="caption" color={hasDataError ? '#f59e0b' : '#10b981'} fontWeight={600}>
                      {hasDataError ? 'Degraded' : 'Verified'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  )
}
